import { loginApi } from "@/api/services/main/auth";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { loginSchema } from "@/features/auth/schema/loginSchema";
import { TLoginForm } from "@/features/auth/types/loginForm";
import { useCacheLocal } from "@/hooks/useCacheLocal";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { pick } from "@/utils/object";
import { getQueryFromURL } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography, styled } from "@mui/material";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { IconSignIn } from "@/assets/icons/IconSignIn";
import { background, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Linkable } from "@/components/elements/Linkable";

type TQuery = {
  email?: string;
  ownerId?: string;
  redirectUrl?: string;
};

export const LoginForm = () => {
  const cacheLocal = useCacheLocal("login-form");
  const navigate = useNavigate();
  const query = getQueryFromURL<TQuery>();

  const defaultValues = useMemo((): TLoginForm => {
    if (query?.ownerId) {
      return { ownerId: query.ownerId, email: query.email ?? "", password: "" };
    }
    if (cacheLocal.getJSON()) {
      return { ...pick(cacheLocal.getJSON(), "ownerId", "email"), password: "" };
    }
    return { ownerId: "", email: "", password: "" };
  }, [cacheLocal, query]);

  const methods = useForm<TLoginForm>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(loginSchema),
  });
  const { control } = methods;
  const [cached, setCached] = useState(!!cacheLocal.getJSON()?.ownerId || !!cacheLocal.getJSON()?.email);

  const onSubmit = async (data: TLoginForm) => {
    try {
      await loginApi(data);
      if (cached) {
        cacheLocal.setJSON(pick(data, "ownerId", "email"));
      } else {
        cacheLocal.remove();
      }
      if (query?.redirectUrl) {
        navigate(query.redirectUrl);
      } else {
        navigate(AppRoutes.home);
      }
    } catch (error) {
      console.log(error);
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleToggleCache = (_: unknown, checked: boolean) => {
    setCached(checked);
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={loginSchema}>
      <Stack gap={3} width={400}>
        <FormField control={control} name="ownerId">
          <Input size="lg" placeholder="入力してください" />
        </FormField>
        <FormField control={control} name="email">
          <Input type="email" size="lg" placeholder="入力してください" />
        </FormField>
        <FormField control={control} name="password">
          <Input type="password" size="lg" />
        </FormField>
        <Checkbox onChange={handleToggleCache} checked={cached} label="組織IDとメールアドレスを保存する" />
        <StyledButton type="submit" variant="primary" fullWidth startIcon={<IconSignIn />}>
          ログイン
        </StyledButton>
        <Linkable href={AppRoutes.recover} textAlign="center" mt={1}>
          <Typography variant="sub16Semi" color="#1890FF">
            パスワードを忘れた方はこちら
          </Typography>
        </Linkable>
        <Box
          sx={{
            bgcolor: background.primary,
            border: `1px solid ${divider.middle}`,
            borderRadius: rounded.sm,
            mt: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              bgcolor: "#ECF9F9",
              width: "100%",
              py: 1,
              borderBottom: `1px solid ${divider.middle}`,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="body14Bold" color={text.primary} sx={{ textAlign: "center", width: "100%" }}>
              動作環境
            </Typography>
          </Box>
          <Box sx={{ p: 2.5 }}>
            <Stack gap={0.75}>
              <Box>
                <Typography variant="body14" color={text.secondary}>
                  • Google Chrome または Microsoft Edge 最新版
                </Typography>
                <Typography 
                  variant="body14" 
                  color={text.secondary} 
                  sx={{ 
                    pl: 3, 
                    mt: 0.5,
                    display: "block"
                  }}
                >
                  注意:製造元のサポートが終了しているOSやWebブラウザー等 は動作環境の対象外となります。
                </Typography>
              </Box>
              <Typography variant="body14" color={text.secondary}>
                • ノートPC (1366 x 768px) 以上のディスプレイ解像度
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Stack>
    </Form>
  );
};

const StyledButton = styled(Button)`
  .MuiButton-startIcon {
    svg {
      transition: color 0.2s ease;
    } 
  &:hover {
    .MuiButton-startIcon {
      svg {
        color: #48A0B2;
      }
    }
  }
  &:active {
    .MuiButton-startIcon {
      svg {
        color: #48A0B2;
      }
    }
  }
`;
